import { Modal } from 'antd';
import { useEffect, useState } from 'react';

import { InfoCircleFilled } from '@ant-design/icons';

import * as St from './ChipMinimizer.styles';
import { hasChipMinimizer } from './ChipMinimizer.utils';

import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { IMAGE_COMING_SOON, NO_CHIP_MINIMIZER } from '~/core/constants';
import { DesignSection, ProductType } from '~/core/enums';
import { useGetDesignSectionTranslations } from '~/core/hooks/useGetDesignSectionTranslations';
import { DrawingShape } from '~/core/models';
import { imgUrl } from '~/core/utils';
import { useChipMinimizerContext, useDrawingShapeContext } from '~contexts';
import DesignStepContainer from '~molecules/DesignStepContainer';
import ApplianceCard from '~organisms/ApplianceCard';

const ChipMinimizer = () => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showChipMinimizer, setShowChipMinimizer] = useState(false);
  const {
    getters: { drawingShapes },
  } = useDrawingShapeContext();
  const { chipMinimizer } = useOptionalItems();
  const {
    selectedChipMinimizers,
    setSelectedChipMinimizers,
    resetSelectedChipMinimizers,
    restorableChipMinimizers,
    resetRestorableChipMinimizers,
  } = useChipMinimizerContext();

  const updateQty = (productId: number, value = 1) => {
    setSelectedChipMinimizers({
      ...selectedChipMinimizers,
      [productId]: {
        isSelected: value > 0,
        isManuallyChanged: false,
        quantity: Math.max(value, 0),
      },
    });
  };

  // Restore chip minimizer selections when loading saved estimate
  useEffect(() => {
    if (chipMinimizer && Object.keys(restorableChipMinimizers).length > 0) {
      const restorableItem = restorableChipMinimizers[chipMinimizer.productId];

      if (restorableItem) {
        setSelectedChipMinimizers({
          [chipMinimizer.productId]: restorableItem,
        });
      }
      resetRestorableChipMinimizers();
    }
  }, [
    chipMinimizer,
    restorableChipMinimizers,
    setSelectedChipMinimizers,
    resetRestorableChipMinimizers,
  ]);

  useEffect(() => {
    if (chipMinimizer) {
      const appliances = drawingShapes
        .flatMap((drawingShape: DrawingShape) => drawingShape.appliances)
        .map((drawingAppliance) => drawingAppliance.applianceModel);

      setShowChipMinimizer(appliances.some(hasChipMinimizer));
    }
  }, [drawingShapes, chipMinimizer, resetSelectedChipMinimizers]);

  useEffect(() => {
    if (chipMinimizer && !showChipMinimizer) {
      resetSelectedChipMinimizers();
    }
  }, [chipMinimizer, resetSelectedChipMinimizers, showChipMinimizer]);

  const { chipMinimizerModify, chipMinimizerTitle, chipMinimizerDescription } =
    useGetDesignSectionTranslations(DesignSection.SINKS, [
      'CHIP_MINIMIZER.MODIFY',
      'CHIP_MINIMIZER.TITLE',
      'CHIP_MINIMIZER.DESCRIPTION',
    ]);

  if (!chipMinimizer || !showChipMinimizer) {
    return null;
  }

  const chipMinimizerQuantity =
    selectedChipMinimizers[chipMinimizer.productId]?.quantity || 0;

  return (
    <>
      <p>
        <St.Title>{chipMinimizerTitle}</St.Title>
        <InfoCircleFilled
          className="ChipMinimizerSection__info-icon"
          onClick={() => setShowInfoModal(true)}
        />
      </p>
      <DesignStepContainer>
        <ApplianceCard
          applianceType={ProductType.CHIP_MINIMIZER}
          title={chipMinimizerModify}
          image={imgUrl(chipMinimizer.imageUrl || IMAGE_COMING_SOON, 300)}
          priceWithoutDiscount={chipMinimizer.price}
          counterProps={{
            quantity: chipMinimizerQuantity,
            onIncrease: () => {
              updateQty(chipMinimizer.productId, chipMinimizerQuantity + 1);
            },
            onDecrease: () => {
              updateQty(chipMinimizer.productId, chipMinimizerQuantity - 1);
            },
          }}
        />
        <ApplianceCard
          applianceType={ProductType.CHIP_MINIMIZER}
          title={NO_CHIP_MINIMIZER.name}
          image={NO_CHIP_MINIMIZER.imageUrl}
          priceWithoutDiscount={0}
          counterProps={{
            quantity: selectedChipMinimizers[NO_CHIP_MINIMIZER.productId]
              ?.isSelected
              ? 1
              : 0,
            onIncrease: () => updateQty(NO_CHIP_MINIMIZER.productId),
            onDecrease: () => updateQty(NO_CHIP_MINIMIZER.productId),
          }}
        />
      </DesignStepContainer>
      <Modal
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        width="500px"
        centered
        footer={null}
        closable
      >
        <span>{chipMinimizerDescription}</span>
      </Modal>
    </>
  );
};

export default ChipMinimizer;
